// src/pages/admin/AdvancePayment.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import API from "../../api";
import { chooseDefaultPaymentMethod, getStaffPaymentMethods } from "../../api/paymentMethods";
import { listStudents, listStaff, listManagerActiveStudents } from "../../api/users";
import { topupWallet, fetchWallet } from "../../api/wallet";
import WalletSummary from "../../components/WalletSummary";
import { formatCurrency, getCurrencySymbol, isCurrencySymbolBefore } from "../../utils/currency";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import { useRegionalPaymentDefaults } from "../../context/RegionalSettingsContext";
import { studentDisplayName } from '../../utils/displayNames';
import { apiErrorMessage } from '../../utils/apiError';

export default function AdminAdvancePayment({ managerMode = false }) {
  const { t } = useTranslation();
  const currencySymbol = getCurrencySymbol();
  const symbolBefore = isCurrencySymbolBefore();
  const { defaultPaymentMethod, paymentReferencePlaceholder } = useRegionalPaymentDefaults();
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(searchParams.get("studentId") || "");
  const [studentSearch, setStudentSearch] = useState("");
  const [amount, setAmount] = useState(() => {
    const suggested = searchParams.get("suggestedAmount");
    const n = Number(suggested);
    return Number.isFinite(n) && n > 0 ? String(n) : "";
  });
  const amountInputRef = useRef(null);
  const topupSectionRef = useRef(null);

  // Sync studentId / suggested amount whenever the query param changes
  useEffect(() => {
    const idFromUrl = searchParams.get("studentId") || "";
    setStudentId(idFromUrl);
    setStudentSearch(""); // clear search so the selected student card shows
    const suggested = searchParams.get("suggestedAmount");
    if (suggested != null && suggested !== "") {
      const n = Number(suggested);
      if (Number.isFinite(n) && n > 0) {
        setAmount(String(n));
      }
    }
  }, [searchParams]);

  // Deep-link from notifications: ensure that student is in the list + focus top-up
  useEffect(() => {
    if (!studentId) return;
    const idNum = Number(studentId);
    if (!Number.isFinite(idNum)) return;

    let cancelled = false;
    (async () => {
      setStudents((prev) => {
        if (prev.some((s) => Number(s.id) === idNum)) return prev;
        return prev;
      });
      try {
        const path = managerMode ? `/manager/users/${idNum}` : `/admin/users/${idNum}`;
        const { data: u } = await API.get(path);
        if (cancelled) return;
        const nameParts = [u.firstName, u.middleName, u.lastName].filter(Boolean);
        const fullName = nameParts.length > 0
          ? nameParts.join(" ")
          : (u.fullName || u.name || u.username || studentDisplayName(null, idNum, t));
        setStudents((prev) => {
          if (prev.some((s) => Number(s.id) === idNum)) return prev;
          return [...prev, { id: idNum, name: fullName }];
        });
      } catch {
        if (cancelled) return;
        setStudents((prev) => {
          if (prev.some((s) => Number(s.id) === idNum)) return prev;
          return [...prev, { id: idNum, name: studentDisplayName(null, idNum, t) }];
        });
      }
    })();

    const timer = setTimeout(() => {
      topupSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      amountInputRef.current?.focus();
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [studentId, managerMode, t]);

  // form fields
  const [mode, setMode] = useState(defaultPaymentMethod);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [reference, setReference] = useState("");
  const [receiverType, setReceiverType] = useState("USER"); // USER | OTHERS
  const [staff, setStaff] = useState([]);
  const [receiverUserId, setReceiverUserId] = useState("");
  const [receiverOtherName, setReceiverOtherName] = useState("");
  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [studentsLoadError, setStudentsLoadError] = useState(false);
  const [staffLoadError, setStaffLoadError] = useState(false);
  const [methodsLoadError, setMethodsLoadError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const paymentMethodRows = await getStaffPaymentMethods();
        setPaymentMethods(paymentMethodRows || []);
        setMode((prev) => chooseDefaultPaymentMethod(paymentMethodRows, defaultPaymentMethod || prev || "CASH"));
        setMethodsLoadError(false);
      } catch (e) {
        console.error("Failed to load payment methods", e);
        setPaymentMethods([]);
        setMethodsLoadError(true);
      }
      try {
        const arr = managerMode
          ? await listManagerActiveStudents()
          : await listStudents({ page: 0, size: 200, q: "" });
        const list = Array.isArray(arr?.content) ? arr.content : Array.isArray(arr) ? arr : (arr?.data || []);
        const opts = list.map((s) => {
          const nameParts = [s.firstName, s.middleName, s.lastName].filter(Boolean);
          const fullName = nameParts.length > 0 ? nameParts.join(' ') : '';
          return {
            id: s.userId || s.id || s.studentId,
            name: s.fullName || s.name || fullName || studentDisplayName(null, s.userId || s.id, t),
          };
        });
        setStudents((prev) => {
          const selectedId = Number(studentId);
          const keep = Number.isFinite(selectedId)
            ? prev.filter((s) => Number(s.id) === selectedId && !opts.some((o) => Number(o.id) === selectedId))
            : [];
          return [...keep, ...opts];
        });
        setStudentsLoadError(false);
      } catch (e) {
        console.error("Failed to load students", e);
        setStudents([]);
        setStudentsLoadError(true);
      }
      try {
        const staffPath = managerMode ? "/manager/users/staff" : "/admin/users/staff";
        const staffRes = managerMode
          ? await API.get(staffPath)
          : null;
        const staffList = managerMode
          ? (Array.isArray(staffRes?.data) ? staffRes.data : (staffRes?.data?.content || []))
          : await listStaff();
        const opts = (staffList || []).map(u => {
          const nameParts = [u.firstName, u.middleName, u.lastName].filter(Boolean);
          const fullName = nameParts.length > 0 ? nameParts.join(' ') : '';
          return { id: u.userId || u.id, display: u.display || u.name || fullName || ('#' + (u.userId || u.id)) };
        });
        setStaff(opts);
        setStaffLoadError(false);
      } catch (e) {
        console.error("Failed to load staff", e);
        setStaff([]);
        setStaffLoadError(true);
      }
    })();
  }, [managerMode, defaultPaymentMethod]);

  useEffect(() => {
    (async () => {
      if (!studentId) { setWallet(null); return; }
      try {
        const w = await fetchWallet(Number(studentId), { managerMode });
        setWallet(w);
      } catch (e) {
        setWallet(null);
      }
    })();
  }, [studentId, saving, managerMode]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount || 0);
    if (!studentId) { alert(t("pages.walletTopup.chooseStudent")); return; }
    if (amt <= 0) { alert(t("pages.walletTopup.amountRequired")); return; }

    if (receiverType !== "USER" && receiverType !== "OTHERS") {
      alert(t("pages.walletTopup.receiverTypeInvalid"));
      return;
    }
    if (receiverType === "USER") {
      if (!receiverUserId) {
        alert(t("pages.walletTopup.staffRequired"));
        return;
      }
    } else if (receiverType === "OTHERS") {
      if (!receiverOtherName || !receiverOtherName.trim()) {
        alert(t("pages.walletTopup.receiverNameRequired"));
        return;
      }
    }

    // Compose reference to include receiver meta
    let ref = reference && reference.trim() ? reference.trim() : "";
    const meta = receiverType === "USER" ? `RECEIVER=USER#${receiverUserId}` : `RECEIVER=OTHERS:${receiverOtherName.trim()}`;
    ref = ref ? `${ref} [${meta}]` : `[${meta}]`;

    setSaving(true);
    try {
      await topupWallet({
        studentId: Number(studentId),
        amount: amt,
        paymentMode: mode,
        reference: ref,
        createdBy: null,
        receiverType,
        receiverUserId: receiverType === "USER" ? Number(receiverUserId) : null,
        receiverOtherName: receiverType === "OTHERS" ? receiverOtherName.trim() : null,
      }, { managerMode });
      setAmount("");
      setReference("");
      setReceiverOtherName("");
      setReceiverUserId("");
      setRefreshKey(prev => prev + 1);
      alert(t("pages.walletTopup.success", { amount: formatCurrency(amt) }));
    } catch (e) {
      console.error("Top-up failed", e);
      alert("❌ " + (apiErrorMessage(e, t("pages.walletTopup.failed"))));
    } finally {
      setSaving(false);
    }
  };

  const filteredStudents = students.filter(s =>
    !studentSearch || s.name.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const selectedStudent = students.find(s => s.id === Number(studentId));

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4">
      <div className="w-full space-y-6">
        <PageHeaderSection
          eyebrow={t("pages.walletTopup.eyebrow")}
          title={t("pages.walletTopup.title")}
          subtitle={t("pages.walletTopup.subtitle")}
          aside={(
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-2 text-sm font-medium text-primary-700">
              <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              {t("pages.walletTopup.walletBadge")}
            </span>
          )}
        />

        {/* Student Selection */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <label className="block text-sm font-semibold text-gray-700 mb-3">{t("pages.walletTopup.selectStudent")}</label>
          {studentsLoadError && !studentSearch && (
            <p className="mb-3 text-sm text-rose-700" role="alert">
              {t("pages.walletTopup.studentsLoadFailed", {
                defaultValue: "Could not load students. Refresh and try again.",
              })}
            </p>
          )}
          {!studentsLoadError && students.length === 0 && !studentSearch && (
            <p className="mb-3 text-sm text-slate-600" role="status">
              {managerMode
                ? t("pages.walletTopup.noManagedStudents", {
                  defaultValue: "No students in your managed branches yet. Add or enroll students first.",
                })
                : t("pages.walletTopup.noStudents", {
                  defaultValue: "No students found.",
                })}
            </p>
          )}
          <div className="relative">
            <input
              type="text"
              placeholder={t("pages.walletTopup.searchPlaceholder")}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
              value={studentSearch}
              onChange={e => setStudentSearch(e.target.value)}
            />
            <svg className="absolute right-4 top-4 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {studentSearch && (
            <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded-xl">
              {filteredStudents.length > 0 ? (
                filteredStudents.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setStudentId(String(s.id)); setStudentSearch(""); }}
                    className={`w-full px-4 py-3 text-left hover:bg-purple-50 transition-colors flex items-center justify-between ${studentId === String(s.id) ? 'bg-purple-100 border-l-4 border-purple-600' : ''
                      }`}
                  >
                    <div>
                      <div className="font-medium text-gray-900">{s.name}</div>
                      <div className="text-sm text-gray-500">{t("pages.walletTopup.idLabel", { id: s.id })}</div>
                    </div>
                    {studentId === String(s.id) && (
                      <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-rose-700">
                  {studentsLoadError
                    ? t("pages.walletTopup.studentsLoadFailed")
                    : t("pages.walletTopup.noStudents")}
                </div>
              )}
            </div>
          )}

          {selectedStudent && !studentSearch && (
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {selectedStudent.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{selectedStudent.name}</div>
                  <div className="text-sm text-gray-600">{t("pages.walletTopup.studentId", { id: selectedStudent.id })}</div>
                </div>
                <button
                  onClick={() => setStudentId("")}
                  className="px-3 py-1 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  {t("pages.walletTopup.change")}
                </button>
              </div>
            </div>
          )}
        </div>

        {studentId && (
          <div ref={topupSectionRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Topup Form - 2 columns */}
            <div className="lg:col-span-2">
              <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t("pages.walletTopup.addFunds")}</h3>
                    <p className="text-sm text-gray-600">{t("pages.walletTopup.enterPaymentDetails")}</p>
                  </div>
                </div>

                {/* Amount & Mode */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("pages.walletTopup.amount")} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      {symbolBefore && (
                        <span className="absolute left-4 top-3.5 text-gray-500 font-medium">{currencySymbol}</span>
                      )}
                      <input
                        ref={amountInputRef}
                        type="number"
                        className={`w-full py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all text-lg font-semibold ${
                          symbolBefore ? "pl-8 pr-4" : "pl-4 pr-8"
                        }`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        required
                      />
                      {!symbolBefore && currencySymbol && (
                        <span className="absolute right-4 top-3.5 text-gray-500 font-medium">{currencySymbol}</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("pages.walletTopup.paymentMode")} <span className="text-red-500">*</span>
                    </label>
                    {methodsLoadError && (
                      <p className="mb-2 text-xs font-medium text-amber-800" role="status">
                        {t('pages.wallet.methodsLoadFailed', {
                          defaultValue: 'Payment methods could not be loaded. Mode list may be incomplete.',
                        })}
                      </p>
                    )}
                    <select
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all appearance-none bg-white"
                      value={mode}
                      onChange={(e) => setMode(e.target.value)}
                    >
                      {(paymentMethods.length ? paymentMethods : [{
                        methodKey: mode || "CASH",
                        displayName: t('pages.wallet.methodCash', { defaultValue: 'Cash' }),
                      }]).map((method) => (
                        <option key={method.methodKey} value={method.methodKey}>{method.displayName || method.methodKey}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Reference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("pages.walletTopup.reference")}
                    <span className="text-gray-500 font-normal ml-2">{t("pages.walletTopup.referenceOptional")}</span>
                  </label>
                  <input
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder={paymentReferencePlaceholder}
                  />
                </div>

                {/* Payment Received By */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-sm font-semibold text-gray-700">{t("pages.walletTopup.paymentReceivedBy")} <span className="text-red-500">*</span></div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setReceiverType("USER")}
                      className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all ${receiverType === "USER"
                        ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t("pages.walletTopup.staffMember")}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setReceiverType("OTHERS")}
                      className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all ${receiverType === "OTHERS"
                        ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t("pages.walletTopup.otherPerson")}
                      </div>
                    </button>
                  </div>

                  {receiverType === "USER" && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("pages.walletTopup.selectStaff")}</label>
                      {staffLoadError && (
                        <p className="mb-2 text-sm text-rose-700" role="status">{t("pages.walletTopup.staffLoadFailed")}</p>
                      )}
                      <select
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all disabled:bg-gray-100"
                        value={receiverUserId}
                        onChange={(e) => setReceiverUserId(e.target.value)}
                        disabled={staffLoadError}
                      >
                        <option value="">{t("pages.walletTopup.chooseStaff")}</option>
                        {staff.map(u => <option key={u.id} value={u.id}>{u.display}</option>)}
                      </select>
                    </div>
                  )}

                  {receiverType === "OTHERS" && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t("pages.walletTopup.receiverName")}</label>
                      <input
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                        value={receiverOtherName}
                        onChange={(e) => setReceiverOtherName(e.target.value)}
                        placeholder={t("pages.walletTopup.receiverNamePlaceholder")}
                      />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setAmount('');
                      setReference('');
                      setReceiverOtherName('');
                      setReceiverUserId('');
                    }}
                  >
                    {t("pages.walletTopup.clearForm")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold hover:from-green-600 hover:to-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg shadow-green-500/30"
                    disabled={saving}
                  >
                    {saving ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t("pages.walletTopup.processing")}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t("pages.walletTopup.recordPayment")}
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Wallet Summary - 1 column */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <WalletSummary studentId={Number(studentId)} key={refreshKey} managerMode={managerMode} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}